import { useState, useCallback, useEffect, useRef } from 'react';
import { generateRandomQuestions, extractShortTestName } from '../utils/questionGenerator';
import { validateTestConfig } from '../utils/testConfigValidator';
import { ERROR_MESSAGES } from '../utils/errorMessages';
import logger from '../../../common/logger';

/**
 * Хук для управления конфигурацией теста
 * @param {object} testData - Данные всех тестов
 * @param {string} currentTestName - Текущий выбранный тест
 * @returns {object} Состояние и методы конфигурации
 */
export const useTestConfiguration = (testData, currentTestName) => {
    const [difficulty, setDifficulty] = useState('');
    const [withoutTimer, setWithoutTimer] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        difficulty: null,
        test: null,
    });

    // Отслеживаем предыдущее значение теста
    const prevTestName = useRef(currentTestName);

    /**
     * Очищает ошибку валидации теста при изменении выбранного теста
     * Оптимизировано: обновляем состояние только если есть ошибка
     */
    useEffect(() => {
        const testChanged = prevTestName.current !== currentTestName;
        prevTestName.current = currentTestName;

        if (testChanged && currentTestName) {
            // Обновляем состояние только если есть ошибка теста
            setValidationErrors(prev => {
                if (prev.test !== null) {
                    return { ...prev, test: null };
                }
                return prev; // Не вызываем ре-рендер если ошибки нет
            });
        }
    }, [currentTestName]);

    /**
     * Обработчик изменения сложности
     */
    const handleDifficultyChange = useCallback((value) => {
        setDifficulty(value);
        setValidationErrors(prev => {
            if (prev.difficulty !== null) {
                return { ...prev, difficulty: null };
            }
            return prev;
        });
    }, []);

    /**
     * Обработчик изменения опции таймера
     */
    const handleTimerToggle = useCallback((checked) => {
        setWithoutTimer(checked);
    }, []);

    /**
     * Создает конфигурацию теста
     */
    const createTestConfig = useCallback(() => {
        const validation = validateTestConfig({
            difficulty,
            testName: currentTestName,
        });

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            return null;
        }

        const shortTestName = extractShortTestName(currentTestName);
        const questions = generateRandomQuestions(
            testData[shortTestName],
            difficulty,
            20
        );

        if (questions.length === 0) {
            logger.error('Не удалось сгенерировать вопросы');
            setValidationErrors({
                difficulty: null,
                test: ERROR_MESSAGES.NO_QUESTIONS,
            });
            return null;
        }

        return {
            nameTest: shortTestName,
            descTest: currentTestName,
            optionTest: {
                diffical: difficulty,
                timer: withoutTimer,
                questions,
                validDif: true,
                validTest: true,
            },
        };
    }, [difficulty, currentTestName, withoutTimer, testData]);

    /**
     * Сбрасывает конфигурацию
     */
    const resetConfiguration = useCallback(() => {
        setDifficulty('');
        setWithoutTimer(false);
        setValidationErrors({ difficulty: null, test: null });
    }, []);

    return {
        difficulty,
        withoutTimer,
        validationErrors,
        handleDifficultyChange,
        handleTimerToggle,
        createTestConfig,
        resetConfiguration,
    };
};
